import { formatSignedCentsPerLb, formatUsdPerLb } from "./callForm";
import type { Headline, Note, PublisherView } from "./types";

export type ExpectationMeasure = PublisherView["measureCode"];

export interface ExpectationSpread {
  measureCode: ExpectationMeasure;
  lowestValue: number;
  highestValue: number;
  publisherCount: number;
}

export const EXPECTATION_MEASURE_LABELS: Record<ExpectationMeasure, string> = {
  Change: "Monthly change",
  Level: "Price level",
};

const MEASURE_ORDER: ExpectationMeasure[] = ["Change", "Level"];

function isActive(record: { status?: "Active" | "Voided" }): boolean {
  return record.status !== "Voided";
}

function newestRecordedFirst(first: { recordedUtc?: string }, second: { recordedUtc?: string }): number {
  return (second.recordedUtc ?? "").localeCompare(first.recordedUtc ?? "");
}

function headlineSearchFields(headline: Headline): (string | null | undefined)[] {
  return [
    headline.title,
    headline.publisherName,
    headline.regionCode,
    headline.designationCode,
    headline.baseCode,
    headline.additiveCode,
    headline.colorCode,
  ];
}

export function filterHeadlines(headlines: Headline[], filterText: string): Headline[] {
  const searchText = filterText.trim().toLowerCase();
  return headlines
    .filter(isActive)
    .filter(
      (headline) =>
        !searchText ||
        headlineSearchFields(headline).some((field) => field?.toLowerCase().includes(searchText)),
    )
    .sort(
      (first, second) =>
        second.publishedDate.localeCompare(first.publishedDate) || newestRecordedFirst(first, second),
    );
}

function isLaterExpectation(candidate: PublisherView, current: PublisherView): boolean {
  if (candidate.asOfDate !== current.asOfDate) return candidate.asOfDate > current.asOfDate;
  return (candidate.recordedUtc ?? "") > (current.recordedUtc ?? "");
}

export function latestExpectationPerPublisher(expectations: PublisherView[]): PublisherView[] {
  const latestByPublisherAndMeasure = new Map<string, PublisherView>();
  for (const expectation of expectations.filter(isActive)) {
    const publisherKey = `${expectation.publisherName.trim().toLowerCase()}|${expectation.measureCode}`;
    const currentLatest = latestByPublisherAndMeasure.get(publisherKey);
    if (!currentLatest || isLaterExpectation(expectation, currentLatest)) {
      latestByPublisherAndMeasure.set(publisherKey, expectation);
    }
  }
  return [...latestByPublisherAndMeasure.values()].sort(
    (first, second) =>
      MEASURE_ORDER.indexOf(first.measureCode) - MEASURE_ORDER.indexOf(second.measureCode) ||
      first.publisherName.localeCompare(second.publisherName),
  );
}

export function expectationSpreads(latestExpectations: PublisherView[]): ExpectationSpread[] {
  return MEASURE_ORDER.flatMap((measureCode) => {
    const expectationsForMeasure = latestExpectations.filter(
      (expectation) => expectation.measureCode === measureCode,
    );
    if (expectationsForMeasure.length === 0) return [];
    return [
      {
        measureCode,
        lowestValue: Math.min(...expectationsForMeasure.map((expectation) => expectation.lowValue)),
        highestValue: Math.max(...expectationsForMeasure.map((expectation) => expectation.highValue)),
        publisherCount: expectationsForMeasure.length,
      },
    ];
  });
}

export function formatExpectationValue(measureCode: ExpectationMeasure, value: number): string {
  return measureCode === "Change" ? formatSignedCentsPerLb(value) : formatUsdPerLb(value);
}

export function formatExpectationRange(
  measureCode: ExpectationMeasure,
  lowValue: number,
  highValue: number,
): string {
  if (lowValue === highValue) return formatExpectationValue(measureCode, lowValue);
  return `${formatExpectationValue(measureCode, lowValue)} to ${formatExpectationValue(measureCode, highValue)}`;
}

export function activeNotesNewestFirst(notes: Note[]): Note[] {
  return notes.filter(isActive).sort(newestRecordedFirst);
}
